import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import delve from 'dlv';

import useSearchOnItems from 'hooks/useSearchOnItems';

import MenuRoot from '../index';
import SearchField, {
  SearchFieldContainer,
} from 'components/styled/SearchField/SearchField';
import { ItemWrapper } from '../Menu';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import useListKeyboardNavigation from '../useKeyboardNavigation';
import sortByDisplayName from 'helpers/sortByDisplayName';
import {
  Figure,
  HelperText,
  ISearchableMenuProps,
  LayoutWrapper,
  ListsScrollableWrapper,
} from '../SearchableMenu';
import {
  ButtonBack,
  CaretBack,
} from 'components/TopicFilters/ViewRelatedMenu/ViewRelatedMenu.styles';
import { ReactComponent as CloseIcon } from 'icons/icon-close.svg';
import Span from 'components/elements/span';
import { ListItemBox } from '../../TopicFiltersDropdown/CountryDropdown/ExtendedCountryDropdown';
import { ReactComponent as ClearButton } from 'icons/icon-clear-button.svg';

const BackButtonSection = styled.div`
  height: 53px;
  display: flex;
  align-items: center;

  @media screen and (min-width: 47em) {
    border-bottom: 1px solid ${colors.LightGrey};
    margin-bottom: 3px;
  }

  ${ButtonBack} {
    margin: 0 0 0 25px;
  }
`;

const ClearButtonCloseIcon = styled(CloseIcon)`
  transform: translateY(0px) scale(0.7);
  color: ${({ theme }): string => theme.colors.AccentGreen};
  margin-bottom: 1px;

  g {
    stroke-width: 3px;
  }
`;

export const Section = styled(Box)<any>`
  width: 50%;
  height: ${({ height }): string => (height ? height : '500px')};
  padding: 0 1em;

  @media screen and (max-width: ${({ smallScreenMeasure }): string =>
      smallScreenMeasure ? smallScreenMeasure : '47em'}) {
    width: 100%;
    height: 465px;

    &:nth-of-type(2) {
      border-top: 1px solid ${colors.LightGrey};
      height: auto;
      padding-bottom: 1em;
    }
  }

  :first-of-type {
    border-right: 1px solid ${colors.LightGrey};
  }

  ${({ customMobile }): string =>
    customMobile
      ? `
    border: none;
    border-bottom: 1px solid ${colors.LightGrey};
    label {
      margin: 0 !important;
    }
    :first-of-type {
      border-right: none;
      height: 500px !important;
    }
    &:nth-of-type(2) {
      border-top: none;
      padding-bottom: 0;
    }
  `
      : ''}

  figcaption {
    span {
      font-size: 11px;
      margin-left: 0.25em;
    }
  }

  ${SearchFieldContainer} {
    margin: 0 0.25em 0.25em !important;
  }

  ${ListsScrollableWrapper} {
    max-height: 24em;

    @media screen and (max-width: 47em) {
      max-height: 23em;
    }

    ${ListItemBox} {
      margin-right: 0.25rem;
      margin-left: 0.25rem;
      padding-left: 0.25em;
      padding-right: 0.1em;
    }
  }
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 10px;
    color: ${colors.NightBlue};

    g {
      stroke-width: 2px;
    }
  }
`;

export const Input = styled('input')<{ invalid?: boolean }>`
  appearance: none;
  box-shadow: none;
  outline: none;
  width: 100%;
  font: inherit;
  height: 40px;
  display: flex;
  align-content: center;
  border: 1px solid
    ${({ theme, invalid }): string =>
      invalid ? theme.NegativeChange : theme.LightGrey};
  padding: 0 12px;
  font-size: 14px;

  ::placeholder {
    color: #8ea6be;
  }

  ::-ms-clear {
    display: none;
  }
`;

const SaveButton = styled.button<{ isActive?: boolean }>`
  height: 40px;
  width: 100%;
  background: ${colors.AccentGreen};
  opacity: ${({ isActive }): number => (isActive ? 1 : 0.4)};
  color: ${colors.White};
  font-size: 14px;
  margin-top: 9px;
`;

export const CancelButton = styled(SaveButton)`
  background: ${colors.Sand};
  color: ${colors.MediumGrey};
`;

const DeleteButton = styled.button`
  font-size: 14px;
  margin-top: 5px;
  background: transparent;
  height: 40px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    margin-right: 4px;

    path {
      fill: ${colors.NightBlue};
    }
  }
`;

const ItemsWrapper = styled.div<{ isSmall?: any }>`
  max-height: ${({ isSmall }): string => (isSmall ? '252px' : '280px')};
  overflow-y: auto;
`;

export const ErrorMessage = styled.div`
  color: ${colors.NegativeChange};
  font-size: 12px;
  margin-top: 8px;
`;

interface ICreateViewMenuProps extends ISearchableMenuProps {
  returnBack: () => void;
  onRemoveItem: (item: any) => void;
  checkIsInvalidInput: (input: string) => boolean;
  inputPlaceholder?: string;
  inputErrorMessage?: string;
  onSave: (inputValue: string, selectedItems: any[]) => void;
  onDelete: () => void;
  selectedCountries: any[];
  editedRegion?: any;
}

const CreateViewSearchableMenu: React.FC<ICreateViewMenuProps> = (props) => {
  const {
    items,
    selectedCountries,
    Item,
    ListLabel,
    itemToString,
    getMenuProps,
    getItemProps,
    entityColorScale,
    additionalFindMethod,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem,
    isSmallScreen,
    isMultipleSelection,
    textToSelect,
    onRemoveItem,
    returnBack,
    checkIsInvalidInput,
    inputPlaceholder,
    inputErrorMessage,
    onSave,
    onDelete,
    editedRegion,
  } = props;

  const [searchValue, setSearchValue] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredItems = useSearchOnItems(
    items,
    searchValue,
    additionalFindMethod
  );
  const isLengthZero = (item: any): boolean =>
    delve(item, 'children', item).length === 0;
  const noResults = filteredItems.every(isLengthZero);

  useListKeyboardNavigation(
    filteredItems.length,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem
  );

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleSearch = (evt: any): void => {
    setHighlightedIndex(0);
    setSearchValue(evt.target.value);
  };

  const itemsToShow = useMemo(() => {
    if (!isMultipleSelection || searchValue) {
      return filteredItems;
    }

    return items.sort(sortByDisplayName);
  }, [items, searchValue, filteredItems, isMultipleSelection]);

  const [isInputInvalid, setIsInputInvalid] = useState<boolean>(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);

  const [newName, setNewName] = useState<string>(
    editedRegion?.displayName || ''
  );

  useEffect(() => {
    const isCanBeSaved =
      selectedCountries.length > 1 &&
      !isInputInvalid &&
      newName.trim().length > 0;
    if (isButtonEnabled !== isCanBeSaved) {
      setIsButtonEnabled(isCanBeSaved);
    }
  }, [isInputInvalid, newName, selectedCountries, isButtonEnabled]);

  const handleNameChange = ({ target: { value } }: { target: any }): void => {
    setNewName(value);
    const isInvalid = checkIsInvalidInput(value);
    if (isInputInvalid !== isInvalid) {
      setIsInputInvalid(isInvalid);
    }
  };

  const saveChanges = (): void => {
    onSave(newName.trim(), selectedCountries);
  };

  return (
    <LayoutWrapper>
      <Box width={isSmallScreen ? 'auto' : '560px'}>
        <BackButtonSection>
          <ButtonBack
            onClick={(evt: MouseEvent): void => {
              evt.stopPropagation();
              returnBack();
            }}
          >
            <CaretBack />
            &nbsp;&nbsp;Cancel
          </ButtonBack>
        </BackButtonSection>
        <Box display="flex" flexDirection={isSmallScreen ? 'column' : 'row'}>
          <Section>
            <Box
              display="flex"
              flexDirection={isSmallScreen ? 'column-reverse' : 'column'}
            >
              <Figure data-menu-figure={true}>
                <figcaption>
                  <ListLabel>CREATE CUSTOM REGION</ListLabel>
                </figcaption>
              </Figure>

              <Box>
                <SearchField
                  refObj={searchRef}
                  handleSearch={handleSearch}
                  value={searchValue}
                  placeholder="Search"
                />
              </Box>
            </Box>

            {!noResults && (
              <ListsScrollableWrapper
                {...getMenuProps({ 'data-menu-list': true })}
              >
                {(!!itemsToShow || searchValue) &&
                  itemsToShow.map((item, index) => (
                    <ItemWrapper
                      key={itemToString(item)}
                      {...getItemProps({
                        'data-menu-list-item': true,
                        key: itemToString(item),
                        item: item,
                        isActive: index === highlightedIndex,
                      })}
                    >
                      <Item
                        item={item}
                        index={index}
                        isHovered={index === highlightedIndex}
                        isActive={
                          isMultipleSelection
                            ? selectedCountries &&
                              selectedCountries
                                .map((i: any): any => itemToString(i))
                                .includes(itemToString(item))
                            : itemToString(selectedCountries) ===
                              itemToString(item)
                        }
                        entityColorScale={entityColorScale}
                        isSmallScreen={isSmallScreen}
                      />
                    </ItemWrapper>
                  ))}
              </ListsScrollableWrapper>
            )}

            {noResults && (
              <HelperText>
                Didn&apos;t find what you&apos;re looking for? <br /> Try again
                with a different search term or try a different spelling.
              </HelperText>
            )}
          </Section>
          <Section
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            {!isSmallScreen ? (
              <Box>
                <Figure data-menu-figure={true}>
                  <figcaption>
                    <ListLabel>SELECTED</ListLabel>
                  </figcaption>
                </Figure>
                <ItemsWrapper
                  isSmall={
                    (inputErrorMessage && isInputInvalid) || editedRegion
                  }
                >
                  {selectedCountries.map((item: any) => (
                    <SelectedItem key={`country-id-${item.id}`}>
                      <ClearButtonCloseIcon
                        onClick={(): void => onRemoveItem(item)}
                      />
                      <Span>{item.displayName}</Span>
                    </SelectedItem>
                  ))}
                </ItemsWrapper>
              </Box>
            ) : null}
            <Box pb="10px">
              <Figure data-menu-figure={true}>
                <figcaption>
                  <ListLabel>{`${textToSelect} NAME`}</ListLabel>
                </figcaption>
              </Figure>
              <Input
                placeholder={inputPlaceholder}
                onChange={handleNameChange}
                invalid={isInputInvalid}
                value={newName}
              />
              {inputErrorMessage && isInputInvalid && (
                <ErrorMessage>{inputErrorMessage}</ErrorMessage>
              )}
              <SaveButton
                isActive={isButtonEnabled}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onClick={isButtonEnabled ? saveChanges : (): void => {}}
              >
                {editedRegion ? 'Save Changes' : 'Create Region'}
              </SaveButton>
              {editedRegion && (
                <DeleteButton onClick={onDelete}>
                  <ClearButton /> Delete Region
                </DeleteButton>
              )}
            </Box>
          </Section>
        </Box>
      </Box>
    </LayoutWrapper>
    // <SearchableMenu {...props} />
  );
};

const CreateCustomRegionMenu: React.FC<any> = (props) => {
  return (
    <MenuRoot {...props}>
      {(renderProps): any => (
        <CreateViewSearchableMenu {...props} {...renderProps}>
          {props.children}
        </CreateViewSearchableMenu>
      )}
    </MenuRoot>
  );
};

export default CreateCustomRegionMenu;
